import { Injectable, Injector } from '@angular/core';
import { IOrder } from '@interfaces/orders';
import * as moment from 'moment-timezone';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, first, filter } from 'rxjs/operators';

import { orderTypesShortES, PASSIVE_MOVEMENT_LABEL, CORPORATE_EVENT_TYPE_LABELS, REFERRAL_RETAINED_DAYS } from '@constants/miscellaneous';
import { IGeneralConstants } from '@interfaces/general-constants';
import { IKhipuTransfer } from '@interfaces/khipu';
import {
  IMovement,
  IDeposit,
  IContributionStrategy,
  IWithdrawal,
  IDepositRedistribution,
  IPassiveMovement,
  IBuyDelete,
  IWithdrawalDelete,
  IDocumentDeleteStatus,
  IDocumentDeleteCollections,
} from '@interfaces/movements';
import Utils from '@util/util';
import { ErrorAndLoadingService } from '../error-loading/error-loading.service';
import { FirebaseService } from '../firebase/firebase.service';
import {
  hasExecutedSellAmountUSD,
  isBrokerRejectedDeposit,
  isBrokerRejectedOrder,
  isBrokerRejectedWithdrawal,
} from './movement-outcome';

@Injectable({
  providedIn: 'root',
})
export class MovementsService {
  hasPendingDistributionMovementSub: any;
  private hasPendingDistributionMovementSubSource = new BehaviorSubject<IDeposit[]>(null);
  currenthasPendingDistributionMovementSubSource = this.hasPendingDistributionMovementSubSource.asObservable();
  unreadDepositsSub: any;
  private unreadDepositsSubSource = new BehaviorSubject<IDeposit[]>(null);
  currentUnreadDepositsSubSource = this.unreadDepositsSubSource.asObservable();
  hasFirstMovementSub: any;
  private hasFirstMovementMovementSubSource = new BehaviorSubject<IDeposit[]>(null);
  currentHasFirstMovementSubSource = this.hasFirstMovementMovementSubSource.asObservable();
  contributionStrategySub: any;
  private contributionStrategySubSource = new BehaviorSubject<IContributionStrategy>(null);
  currentContributionStrategySubSource = this.contributionStrategySubSource.asObservable();
  depositsInProgressAmountSub: any;
  private depositsInProgressAmountSource = new BehaviorSubject<number>(0);
  currentDepositsInProgressAmountSource = this.depositsInProgressAmountSource.asObservable();
  reloadHomeChart = false;
  movements: {[key: string]: BehaviorSubject<any[]>};
  private pendingOrdersSubSource = new BehaviorSubject<IOrder[]>(null);
  currentPendingOrdersSubSource = this.pendingOrdersSubSource.asObservable();
  pendingOrdersSub: any;
  private pendingOrdersSumAmountUSDSource = new BehaviorSubject<number>(null);
  currentPendingOrdersSumAmountUSDSource = this.pendingOrdersSumAmountUSDSource.asObservable();
  cashFromSellsInTransitSub: any;
  private cashFromSellsInTransitSubSource = new BehaviorSubject<number>(0);
  currentCashFromSellsInTransitSubSource = this.cashFromSellsInTransitSubSource.asObservable();
  dwTradesSub: any;
  private dwTradesSubSource = new BehaviorSubject<IOrder[]>(null);
  currentDwTradesSubSource = this.dwTradesSubSource.asObservable();
  generalConstants: IGeneralConstants;
  private assetsService: any;
  private racionalStocksService: any;
  private authService: any;

  // Dividends API cache
  private dividendsCache: {
    [key: string]: {
      data: BehaviorSubject<IPassiveMovement[]>;
      timestamp: number;
      params: any;
    },
  } = {};
  // Corporate events API cache
  private corporateEventsCache: {
    [key: string]: {
      data: BehaviorSubject<IPassiveMovement[]>;
      timestamp: number;
      params: any;
    },
  } = {};
  private CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(
    private firebaseService: FirebaseService,
    private errorService: ErrorAndLoadingService,
    private injector: Injector,
  ) {
    this.movements = {};
    this.calculatePendingOrdersSumAmountUSD();
    this.firebaseService.currentGeneralConstants.subscribe(generalConstants => this.generalConstants = generalConstants);
    this.initializeAssetsService();
  }

  private async initializeAssetsService() {
    try {
      await this.getAssetsService();
    } catch (error) {
      console.error('Failed to initialize AssetsService:', error);
    }
  }

  private async getAssetsService() {
    if (!this.assetsService) {
      const module = await import('../assets/assets.service');
      this.assetsService = this.injector.get(module.AssetsService);
    }
    return this.assetsService;
  }

  private getAssetsServiceSync() {
    return this.assetsService;
  }

  private async getRacionalStocksService() {
    if (!this.racionalStocksService) {
      const module = await import('../racional-stocks/racional-stocks.service');
      this.racionalStocksService = this.injector.get(module.RacionalStocksService);
    }
    return this.racionalStocksService;
  }

  private async getAuthService() {
    if (!this.authService) {
      const module = await import('../auth/auth.service');
      this.authService = this.injector.get(module.AuthService);
    }
    return this.authService;
  }

  calculatePendingOrdersSumAmountUSD() {
    this.currentPendingOrdersSubSource.subscribe(orders => {
      if (orders) {
        let totalCashExcess = 0;
        orders.forEach(order => {
          if (order.isBuy && order.orderType === 'MARKET' && !order.sent) {
            totalCashExcess += Math.max(order.proposedAmountUSD, 0);
          }
        });
        this.pendingOrdersSumAmountUSDSource.next(totalCashExcess);
      }
    });
  }

  getPendingMovementObs(uid: string) {
    this.hasPendingDistributionMovementSub = this.firebaseService.getSnapshotWithUidObs(
      'deposits', 'status', 'waiting_contribution_strategy', this.hasPendingDistributionMovementSubSource, uid,
    );
    this.hasFirstMovementSub = this.firebaseService.getSnapshotWithUidObs(
      'deposits', 'userId', uid, this.hasFirstMovementMovementSubSource, uid,
    );
  }

  getPendingOrdersObs(uid: string) {
    this.pendingOrdersSub = this.firebaseService.getSnapshotWithUidObs(
      'orders', 'status', 'created', this.pendingOrdersSubSource, uid);
  }

  getCashFromSellsInTransit(userId: string, subscription: string) {
    if (subscription !== 'pro') {
      const limitUpperDate = new Date();
      const pastDays = Utils.shouldUseNewWithdrawalDate(
        this.generalConstants.withdrawalsSettlementChangeDate.toDate(),
        limitUpperDate,
        true,
      ) ? 1 : 2;
      const limitBottomDate = this.firebaseService.getPreviousWorkingDate(limitUpperDate, false, true, pastDays);
      const collection = this.firebaseService.getCollection('withdrawals');
      const query = collection.where('userId', '==', userId).where('isSell', '==', true).where('executionDate', '>', limitBottomDate);
      const parseDepositsFn = (querySnapshot) => querySnapshot.docs
        .filter(doc => this.hasSettledSellAmountUSD(doc.data()))
        .map(doc => Math.abs(doc.data().executedShareVariations[0].amountUSD))
        .reduce((a, b) => a + b, 0);
      this.cashFromSellsInTransitSub =
        this.firebaseService.observeQuery(query, this.cashFromSellsInTransitSubSource, parseDepositsFn);
    }
  }

  private hasSettledSellAmountUSD(withdrawal): boolean {
    if (withdrawal.status === 'failed') {
      return false;
    }
    return hasExecutedSellAmountUSD(withdrawal);
  }

  getUnreadDeposits(uid: string) {
    this.unreadDepositsSub = this.firebaseService.getSnapshotWithUidObs('deposits', 'read', false, this.unreadDepositsSubSource, uid);
  }

  getContributionStrategyObs(uid: string) {
   this.contributionStrategySub = this.firebaseService.getDocumentObs('contributionStrategies', uid, this.contributionStrategySubSource);
  }

  getDepositsAmountInProgress(userId: string) {
    const collection = this.firebaseService.getCollection('deposits');
    const terminalStatuses = ['complete', 'failed'];
    const query = collection.where('userId', '==', userId).where('status', 'not-in', terminalStatuses);
    const parseDepositsFn = (querySnapshot) => querySnapshot.docs
      .filter(doc => (!doc.data().transferWithdrawalId || doc.data().transferWithdrawalOnlyCashExcess) && !doc.data().isBuy)
      .map(doc => doc.data().amount)
      .reduce((a, b) => a + b, 0);
    this.depositsInProgressAmountSub = this.firebaseService.observeQuery(query, this.depositsInProgressAmountSource, parseDepositsFn);
  }

  saveContributionStrategy(contributionStrategy: IContributionStrategy, uid: string): Promise<void> {
    return this.firebaseService.addDocumentWithID(contributionStrategy,
      this.firebaseService.getCollection('contributionStrategies'), uid);
  }

  async getOrderById(id: string, uid: string): Promise<IOrder | null> {
    try {
      const collection = this.firebaseService.getCollection('orders');
      const orderRef = await this.firebaseService.getDocumentsWithSingleQuery(collection, 'id', '==', id, uid, true);
      if (orderRef.empty) {
        return null;
      }
      return orderRef.docs[0].data() as IOrder;
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener los movimientos, inténtalo nuevamente', false);
      throw err;
    }
  }

  async getDepositById(id: string): Promise<IDeposit | null> {
    if (this.movements['deposits']) {
      const lastCollectionReceived = this.movements['deposits'].getValue();
      const deposit = lastCollectionReceived && lastCollectionReceived.find(d => d.id === id);
      if (deposit) {
        return deposit;
      }
    }
    try {
      const collection = this.firebaseService.getCollection('deposits');
      const depositRef = await this.firebaseService.getDocumentObject(id, collection);
      if (!depositRef.exists) {
        return null;
      }
      return depositRef.data() as IDeposit;
    } catch (err) {
      throw err;
    }
  }

  async getWithdrawalById(id: string): Promise<IWithdrawal | null> {
    if (this.movements['withdrawals']) {
      const lastCollectionReceived = this.movements['withdrawals'].getValue();
      const withdrawal = lastCollectionReceived && lastCollectionReceived.find(w => w.id === id);
      if (withdrawal) {
        return withdrawal;
      }
    }
    try {
      const collection = this.firebaseService.getCollection('withdrawals');
      const withdrawalRef = await this.firebaseService.getDocumentObject(id, collection);
      if (!withdrawalRef.exists) {
        return null;
      }
      return withdrawalRef.data() as IWithdrawal;
    } catch (err) {
      throw err;
    }
  }

  async getMovementByParentId(parentCollection: string, parentId: string, collectionName: string, id: string): Promise<IPassiveMovement> {

    const collectionPath = parentCollection + '/' + parentId + '/' + collectionName;
    if (this.movements[collectionPath]) {
      const lastCollectionReceived = this.movements[collectionPath].getValue();
      const movement = lastCollectionReceived && lastCollectionReceived.find(m => m.id === id);
      if (movement) {
        return movement;
      }
    }

    if (collectionName === 'corporateEvents' && id.startsWith('corp_')) {
      try {
        const racionalStocksService = await this.getRacionalStocksService();
        const event = await racionalStocksService.getCorporateEventById(id);
        const executionDateSeconds = event.executionDate
          ? Math.floor(new Date(event.executionDate).getTime() / 1000)
          : Math.floor(Date.now() / 1000);
        const typeLabel = CORPORATE_EVENT_TYPE_LABELS[event.actionType] || PASSIVE_MOVEMENT_LABEL['corporateEvents'];
        const displaySymbol = event.actionType === 'SPINOFF' && event.details && event.details.parentSymbol
          ? event.details.parentSymbol
          : event.assetId;
        return {
          ...event,
          type: `${typeLabel} ${displaySymbol}`,
          isPassive: true,
          operation: 'corporateEvents',
          typeClass: 'corporateEvents',
          isCorporateEvent: true,
          actionType: event.actionType,
          positionDelta: event.positionDelta,
          corporateEventDescription: event.description,
          corporateEventDetails: event.details,
          amountUSD: this.extractCorporateEventCashAmount(event),
          amount: 0,
          createdAt: { seconds: executionDateSeconds },
          executionDate: { seconds: executionDateSeconds },
        } as IPassiveMovement;
      } catch (apiErr) {
        return null;
      }
    }

    if (collectionName === 'dividends' && id.startsWith('div_')) {
      try {
        const racionalStocksService = await this.getRacionalStocksService();
        const dividend = await racionalStocksService.getDividendById(id);

        if (parentCollection === 'portfolios' && dividend.portfolioId !== parentId) {
          return null;
        }

        let dateString = dividend.executionDate;
        if (dateString && dateString.includes('_')) {
          dateString = dateString.split('_').pop();
        }
        const executionDateSeconds = dateString
          ? Math.floor(new Date(dateString).getTime() / 1000)
          : Math.floor(Date.now() / 1000);

        return {
          ...dividend,
          type: PASSIVE_MOVEMENT_LABEL['dividends'],
          isPassive: true,
          operation: 'dividends',
          typeClass: 'dividends',
          isUSDDividend: !dividend.assetId,
          createdAt: { seconds: executionDateSeconds },
          executionDate: { seconds: executionDateSeconds },
        };
      } catch (apiErr) {
      }
    }

    try {
      const collection = this.firebaseService.getCollection(collectionPath);
      const movementData = await this.firebaseService.getDocument(id, collection);
      if (movementData) {
        return {
          ...movementData,
          id,
          type: PASSIVE_MOVEMENT_LABEL[collectionName],
          isPassive: true,
          operation: collectionName,
          typeClass: collectionName,
          createdAt: movementData.executionDate || movementData.createdAt,
        } as IPassiveMovement;
      }
    } catch (err) {
      console.error(`Error fetching ${collectionName} ${id}:`, err);
      // Firebase failed
    }

    if (collectionName === 'dividends' || collectionName === 'interests') {
      return null;
    }

    throw new Error(`Movement ${id} not found`);
  }

  async getPassiveMovementDistribution(portfolios, type: string, id: string): Promise<IPassiveMovement[]> {
    try {
      const promises = portfolios.map(({ portfolioId }): Promise<IPassiveMovement> => {
        return this.getMovementByParentId('portfolios', portfolioId, type, id);
      });
      return await Promise.all(promises);
    } catch (err) {
      throw err;
    }
  }

  getContributionsById(id: string, keyToFind: string, userId: string, isDW = false) {
    let observablePath = 'contributions/' + keyToFind + '==' + id;
    if (keyToFind === 'userId') {
      observablePath = 'contributions';
    }
    if (this.movements[observablePath]) {
      return this.movements[observablePath].asObservable();
    }
    try {
      this.movements[observablePath] = new BehaviorSubject<IDeposit[]>(null);
      const collection = this.firebaseService.getCollection('contributions');
      const query = collection.where('userId', '==', userId).where(keyToFind, '==', id);
      const parseContributionsFn = (querySnapshot) => {
        return querySnapshot.docs.map((document) => ({
          ...document.data(),
          id: document.id,
          referral: (document.data().referral || document.data().referred),
          type: ((document.data().referral || document.data().referred) ? 'Premio' : document.data().isBuy ? 'Compra' : 'Depósito') +
                (document.data().referralCommissionDate ? ' por referidos' : '') +
                (isDW && document.data().status !== 'waiting_contribution_strategy'
                ? ` ${this.getAssetsServiceSync() &&
                this.getAssetsServiceSync().getAssetTicker(document.data().proposedShareVariations[0].assetId)
                || document.data().proposedShareVariations[0].assetId}` : ''),
          operation: 'contribution',
          typeClass: document.data().status === 'complete' ? 'contribution' : 'pending-contribution',
        } as IMovement));
      };
      this.firebaseService.observeQuery(query, this.movements[observablePath], parseContributionsFn);
      return this.movements[observablePath].asObservable();
    } catch (err) {
      throw err;
    }
  }

  getDepositsById(id: string, keyToFind: string) {
    let observablePath = 'deposits/' + keyToFind + '==' + id;
    if (keyToFind === 'userId') {
      observablePath = 'deposits';
    }
    if (this.movements[observablePath]) {
      return this.movements[observablePath].asObservable();
    }
    try {
      this.movements[observablePath] = new BehaviorSubject<IDeposit[]>(null);
      const collection = this.firebaseService.getCollection('deposits');
      const query = collection.where(keyToFind, '==', id);
      const parseDepositsFn = (querySnapshot) => {
        return querySnapshot.docs.filter((document) => {
          const deposit = document.data();
          return !isBrokerRejectedDeposit(deposit);
        }).map((document) => ({
          ...document.data(),
          id: document.id,
          referral: document.data().referral,
          referred: document.data().referred,
          type: document.data().status === 'waiting_contribution_strategy' ? '¡Distribuir depósito!' :
                `${(document.data().referral || document.data().referred) ? 'Premio' : document.data().isBuy ? 'Compra' : 'Depósito'}` +
                `${document.data().orderType && document.data().orderType !== 'MARKET' ?
                  ` ${orderTypesShortES[document.data().orderType]}` : ''}` +
                `${(document.data().referralCommissionDate) ? ' por referidos' : ''}` +
                (document.data().status !== 'waiting_contribution_strategy' && document.data().isBuy
                ? ` ${this.getAssetsServiceSync() &&
                this.getAssetsServiceSync().getAssetTicker(document.data().contributionStrategy.uniqueAssetId)
                || document.data().contributionStrategy.uniqueAssetId}` : ''),
          operation: 'contribution',
          typeClass: document.data().status === 'complete' && document.data().isBuy ? 'buy' :
                     document.data().status === 'complete' ? 'contribution' :
                     document.data().status === 'waiting_contribution_strategy' ? 'action-required' :
                     document.data().isBuy ? 'pending_buy' : 'pending-contribution',
        } as IDeposit));
      };
      this.firebaseService.observeQuery(query, this.movements[observablePath], parseDepositsFn);
      return this.movements[observablePath].asObservable();
    } catch (err) {
      throw err;
    }
  }

  getWithdrawalsById(id: string, keyToFind: string, userId: string, isUSD = false) {
    let observablePath = 'withdrawals/' + keyToFind + '==' + id;
    if (keyToFind === 'userId') {
      observablePath = 'withdrawals';
    }
    if (this.movements[observablePath]) {
      return this.movements[observablePath].asObservable();
    }
    try {
      this.movements[observablePath] = new BehaviorSubject<IWithdrawal[]>(null);
      const collection = this.firebaseService.getCollection('withdrawals');
      const query = collection.where('userId', '==', userId).where(keyToFind, '==', id);
      const parseWithdrawalsFn = (querySnapshot) => {
        return querySnapshot.docs.filter((document) => {
          const withdrawal = document.data();
          return !isBrokerRejectedWithdrawal(withdrawal);
        }).map((document) => {
          let dollarsInTransit = false;
          if (document.data().isSell && document.data().status === 'complete') {
            const executionDate = document.data().executionDate.toDate();
            const firstWorkingDay = this.firebaseService.getNextWorkingDay(executionDate, true, true);
            const secondWorkingDay = this.firebaseService.getNextWorkingDay(firstWorkingDay, true, true);
            const dollarsReceivedDate = Utils.shouldUseNewWithdrawalDate(
              this.generalConstants.withdrawalsSettlementChangeDate.toDate(),
              executionDate,
            ) ? firstWorkingDay : secondWorkingDay;
            const morningTime = this.generalConstants.stockExchangeTime.morningDW;
            dollarsReceivedDate.setHours(morningTime[0], morningTime[1]);
            dollarsInTransit = new Date() < dollarsReceivedDate;
          }
          const displayComplete = document.data().status === 'complete' && !dollarsInTransit;
          return {
            ...document.data(),
            id: document.id,
            type: (document.data().isSell ? 'Venta' : 'Retiro') +
            `${document.data().orderType && document.data().orderType !== 'MARKET' ?
            ` ${orderTypesShortES[document.data().orderType]}` : ''}` +
              ((isUSD || document.data().isSell) && document.data().uniqueAssetId ?
              ` ${this.getAssetsServiceSync() && this.getAssetsServiceSync().getAssetTicker(document.data().uniqueAssetId) ||
              document.data().uniqueAssetId}` : ''),
            operation: 'withdrawal',
            typeClass: document.data().isSell && displayComplete ? 'sell' : displayComplete ? 'withdrawal' :
                       document.data().isSell ? 'pending_sell' : 'pending-withdrawal',
            createdAt: document.data().createdAt || new Date(),
          } as IWithdrawal;
        });
      };
      this.firebaseService.observeQuery(query, this.movements[observablePath], parseWithdrawalsFn);
      return this.movements[observablePath].asObservable();
    } catch (err) {
      throw err;
    }
  }

  getAllUserDividends(
    userId: string,
    assetId?: string,
  ): Observable<IPassiveMovement[]> {

    return Observable.combineLatest([
      this.getPortfoliosDividendsFromAPI(null, userId, assetId),
      this.getMovementCollectionByParentId('users', userId, 'dividends'),
    ]).pipe(
      map(([dwDividends, allFirebaseDividends]: [IPassiveMovement[], IPassiveMovement[]]) => {
        // Filter OUT DW dividends from Firebase (they have isDWDividend: true)
        // This prevents duplication since DW dividends come from API
        const clpDividends = (allFirebaseDividends || []).filter(d =>
          d && !d.isDWDividend,
        );

        // Merge DW dividends (from API) + CLP dividends (from Firebase, filtered)
        const allDividends = [
          ...(dwDividends || []),
          ...clpDividends,
        ].filter(d => d);

        return allDividends;
      }),
    );
  }

  getPortfoliosDividendsFromAPI(
    portfolioId: string,
    userId: string,
    assetId?: string,
  ): Observable<IPassiveMovement[]> {

    const cacheKey = this.buildDividendsCacheKey(userId, { assetId });
    const now = Date.now();
    const cached = this.dividendsCache[cacheKey];

    if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
      return cached.data.asObservable();
    }

    const subject = new BehaviorSubject<IPassiveMovement[]>(null);

    const params: any = {};

    if (assetId) {
      params.assetId = assetId;
    }

    this.getRacionalStocksService()
      .then(racionalStocksService => racionalStocksService.getUserDividends(params))
      .then(response => {
        const dividends = response.dividends
          .filter(d => d.assetId !== 'CASH')
          .map(div => {
            let dateString = div.executionDate;
            if (dateString && dateString.includes('_')) {
              dateString = dateString.split('_').pop();
            }

            const executionDateSeconds = dateString
              ? Math.floor(new Date(dateString).getTime() / 1000)
              : Math.floor(Date.now() / 1000);

            return {
              ...div,
              type: PASSIVE_MOVEMENT_LABEL['dividends'],
              isPassive: true,
              operation: 'dividends',
              typeClass: 'dividends',
              isUSDDividend: !div.assetId,
              createdAt: { seconds: executionDateSeconds },
              executionDate: { seconds: executionDateSeconds },
            } as IPassiveMovement;
          });

        this.dividendsCache[cacheKey] = {
          data: subject,
          timestamp: now,
          params,
        };

        subject.next(dividends);
      })
      .catch(async apiErr => {
        this.firebaseService.logError(`Stocks API failed: ${apiErr.message}`);

        try {
            let firestoreDividends = await this.getPortfoliosDividendsFromFirebase(portfolioId, userId)
              .pipe(
                filter(dividends => dividends !== null),
                first(),
              )
              .toPromise();

          if (assetId) {
            firestoreDividends = firestoreDividends.filter(d =>
              d.assetId && d.assetId.toUpperCase() === assetId.toUpperCase(),
            );
          }

          subject.next(firestoreDividends);

        } catch (firestoreErr) {
          this.firebaseService.logError(
            `Both dividend sources failed - API: ${apiErr.message}, Firebase: ${firestoreErr.message}`,
          );
        }
      });

    return subject.asObservable();
  }

  private getPortfoliosDividendsFromFirebase(
    portfolioId: string,
    userId: string,
  ): Observable<IPassiveMovement[]> {
    const collectionPath = 'users/' + userId + '/dividends/portfolioId==' + portfolioId;

    if (this.movements[collectionPath]) {
      return this.movements[collectionPath].asObservable();
    }

    try {
      this.movements[collectionPath] = new BehaviorSubject<IPassiveMovement[]>(null);
      const collection = this.firebaseService.getCollection('users/' + userId + '/dividends');
      const query = collection.where('portfolioId', '==', portfolioId);
      const parsePassiveMovementFn = (querySnapshot) => {
        return querySnapshot.docs
          .filter((doc) => doc.data().assetId !== 'CASH')
          .map((document) => ({
            type: PASSIVE_MOVEMENT_LABEL['dividends'],
            id: document.id,
            isPassive: true,
            operation: 'dividends',
            typeClass: 'dividends',
            createdAt: document.data().executionDate,
            ...document.data(),
          } as IPassiveMovement));
      };

      this.firebaseService.observeQuery(
        query,
        this.movements[collectionPath],
        parsePassiveMovementFn,
      );

      return this.movements[collectionPath].asObservable();
    } catch (err) {
      throw err;
    }
  }

  private buildDividendsCacheKey(userId: string, filters: any): string {
    const parts = ['api_dividends', userId];
    if (filters.assetId) { parts.push('asset', filters.assetId); }
    return parts.join('_');
  }

  private buildCorporateEventsCacheKey(userId: string, filters: any): string {
    const parts = ['api_corporate_events', userId];
    if (filters.assetId) { parts.push('asset', filters.assetId); }
    return parts.join('_');
  }

  private extractCorporateEventCashAmount(event: any): number {
    if (event.details && event.details.cashAmount !== undefined) {
      return event.details.cashAmount;
    }
    if (event.amountUSD !== undefined) {
      return event.amountUSD;
    }
    return 0;
  }

  getUserCorporateEventsFromAPI(
    userId: string,
    assetId?: string,
  ): Observable<IPassiveMovement[]> {
    const cacheKey = this.buildCorporateEventsCacheKey(userId, { assetId });
    const now = Date.now();
    const cached = this.corporateEventsCache[cacheKey];
    if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
      return cached.data.asObservable();
    }
    const subject = new BehaviorSubject<IPassiveMovement[]>([]);
    const params: any = {};
    if (assetId) {
      params.assetId = assetId;
    }
    this.getRacionalStocksService()
      .then(racionalStocksService => racionalStocksService.getUserCorporateEvents(params))
      .then(response => {
        const events = (response.corporateEvents || []).map(event => {
          const executionDateSeconds = event.executionDate
            ? Math.floor(new Date(event.executionDate).getTime() / 1000)
            : Math.floor(Date.now() / 1000);
          const typeLabel = CORPORATE_EVENT_TYPE_LABELS[event.actionType] || PASSIVE_MOVEMENT_LABEL['corporateEvents'];
          const displaySymbol = event.actionType === 'SPINOFF' && event.details && event.details.parentSymbol
            ? event.details.parentSymbol
            : event.assetId;
          return {
            ...event,
            type: `${typeLabel} ${displaySymbol}`,
            isPassive: true,
            operation: 'corporateEvents',
            typeClass: 'corporateEvents',
            isCorporateEvent: true,
            actionType: event.actionType,
            positionDelta: event.positionDelta,
            corporateEventDescription: event.description,
            corporateEventDetails: event.details,
            amountUSD: this.extractCorporateEventCashAmount(event),
            amount: 0,
            createdAt: { seconds: executionDateSeconds },
            executionDate: { seconds: executionDateSeconds },
          } as IPassiveMovement;
        });
        this.corporateEventsCache[cacheKey] = {
          data: subject,
          timestamp: now,
          params,
        };
        subject.next(events);
      })
      .catch(apiErr => {
        this.firebaseService.logError(`Corporate events API failed: ${apiErr.message}`);
        subject.next([]);
      });
    return subject.asObservable();
  }

  getMovementCollectionByParentId(parentCollection: string, parentId: string, collectionName: string) {
    const collectionPath = parentCollection + '/' + parentId + '/' + collectionName;
    if (this.movements[collectionPath]) {
      return this.movements[collectionPath].asObservable();
    }
    try {
      this.movements[collectionPath] = new BehaviorSubject<IPassiveMovement[]>(null);
      const query = this.firebaseService.getCollection(collectionPath);
      const parsePassiveMovementFn = (querySnapshot) => {
        return querySnapshot.docs.filter((doc) => doc.data().assetId !== 'CASH').map((document) => ({
          type: PASSIVE_MOVEMENT_LABEL[collectionName],
          id: document.id,
          isPassive: true,
          operation: collectionName,
          typeClass: collectionName,
          createdAt: document.data().executionDate,
          ...document.data(),
        } as IPassiveMovement));
      };
      this.firebaseService.observeQuery(query, this.movements[collectionPath], parsePassiveMovementFn);
      return this.movements[collectionPath].asObservable();
    } catch (err) {
      throw err;
    }
  }

  async getContributionStrategy(uid: string): Promise<IContributionStrategy | null> {
    return this.firebaseService.getDocumentsWithSingleQuery(
      this.firebaseService.getCollection('contributionStrategies'), 'userId', '==', uid,
    ).then(contributionRef => {
      return contributionRef.empty ? null : (contributionRef.docs[0].data() as IContributionStrategy);
    });
  }

  async hasPendingDistributionMovement(uid: string): Promise<boolean> {
    return this.firebaseService.getDocumentsWithSingleQuery(
      this.firebaseService.getCollection('deposits'), 'status', '==', 'waiting_contribution_strategy', uid, true,
    ).then(movementsRef => {
      if (movementsRef.empty) {
        return false;
      }
      return true;
    });
  }

  async hasFirstMovement(uid: string): Promise<boolean> {
    const collection = this.firebaseService.getCollection('deposits');
    const query = collection.where('userId', '==', uid);
    const isEmpty = await this.firebaseService.isEmptyQuery(query);
    return !isEmpty;
  }

  async portfolioHasMovement(portfolioId: string, userId: string): Promise<boolean> {
    const collection = this.firebaseService.getCollection('contributions');
    const query = collection.where('userId', '==', userId).where('portfolioId', '==', portfolioId);
    const hasContributions = !(await this.firebaseService.isEmptyQuery(query));

    const khipuCollection = this.firebaseService.getCollection('khipuTransfers');
    const paidKhipu = await khipuCollection.where('status', '==', 'paid').where('userId', '==', userId).get();
    const hasPaidKhipu = !!paidKhipu.docs.find(
      doc => (doc.data() as IKhipuTransfer).contributionStrategy.portfolios.find(
        p => p.portfolioId === portfolioId && p.percentage,
      ),
    );
    return hasContributions || hasPaidKhipu;
  }

  async portfolioHasMovementInProgress(portfolioId: string, userId: string): Promise<boolean> {
    const collection1 = this.firebaseService.getCollection('contributions');
    const collection2 = this.firebaseService.getCollection('withdrawals');
    const terminalStatuses = ['complete', 'failed'];
    const query1 = collection1.where('userId', '==', userId).where('portfolioId', '==', portfolioId)
      .where('status', 'not-in', terminalStatuses);
    const query2 = collection2.where('userId', '==', userId).where('portfolioId', '==', portfolioId)
      .where('status', 'not-in', terminalStatuses);
    const isEmpty1 = await this.firebaseService.isEmptyQuery(query1);
    if (!isEmpty1) {
      return true;
    } else {
      const isEmpty2 = await this.firebaseService.isEmptyQuery(query2);
      return !isEmpty2;
    }
  }

  async hasRetainedReferredMoney(uid: string): Promise<boolean> {
    const collection = this.firebaseService.getCollection('deposits');
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - REFERRAL_RETAINED_DAYS);
    const queryReferral = collection.where('userId', '==', uid).where('referral', '==', true).where('createdAt', '>=', daysAgo);
    const queryReferred = collection.where('userId', '==', uid).where('referred', '==', true).where('createdAt', '>=', daysAgo);
    const isEmptyReferral = await this.firebaseService.isEmptyQuery(queryReferral);
    const isEmptyReferred = await this.firebaseService.isEmptyQuery(queryReferred);
    return !isEmptyReferral || !isEmptyReferred;
  }

  createBuy(buy) {
    return new Observable(observer => {
      const collection = this.firebaseService.getCollection('buys');
      this.firebaseService.addDocumentWithID(buy, collection, buy.id)
      .then(() => {
        const unsubscribe = this.firebaseService.firestore.doc(`/buys/${buy.id}`).onSnapshot(
          (doc) => {
            if (doc.data().status === 'valid') {
              observer.next(true);
              unsubscribe();
            } else if (doc.data().status === 'invalid') {
              observer.next(false);
              unsubscribe();
            } else {
              return;
            }
          },
          () => {
            observer.next(false);
            unsubscribe();
          },
        );
      })
      .catch(err => {
        this.firebaseService.logError(err.message);
        observer.next(false);
      });
    });
  }

  createWithdrawal(withdrawal) {
    return new Observable(observer => {
      const collection = this.firebaseService.getCollection('withdrawals');
      Promise.all([this.firebaseService.addDocumentWithID(withdrawal, collection, withdrawal.id)])
      .then(() => {
        const unsubscribe = this.firebaseService.firestore.doc(`/withdrawals/${withdrawal.id}`).onSnapshot(
          (doc) => {
            if (doc.data().amountUSD && !doc.data().usdAmountCalculated) {
              return;
            } else if (doc.data().withdrawalAmountValid === true) {
              observer.next(true);
              unsubscribe();
            }
          },
          () => {
            observer.next(false);
            unsubscribe();
          },
        );
      })
      .catch(err => {
        console.error(err);
        this.firebaseService.logError(err.message);
        observer.next(false);
      });
    });
  }

  deleteBuy(buyDelete: IBuyDelete) {
    return new Observable(observer => {
      const docRef = this.firebaseService.firestore.doc(`${IDocumentDeleteCollections.Buys}/${buyDelete.depositId}`);
      docRef.get().then((docSnapshot) => {
        this.handleDeletedDocumentState(docSnapshot.data(), observer);
      }).catch(_ => {
        this.firebaseService.addDocumentWithID(
          buyDelete, this.firebaseService.getCollection(IDocumentDeleteCollections.Buys), buyDelete.depositId,
        ).then(() => {
          const subscription = docRef.onSnapshot((doc) => {
            if (!doc.exists) {
              observer.next(false);
              subscription();
              return;
            }
            const buyDeleteDoc = doc.data();
            this.handleDeletedDocumentState(buyDeleteDoc, observer);
          },
          () => {
            observer.next(false);
            subscription();
          });
        });
      });
    });
  }

  deleteWithdrawal(withdrawalDelete: IWithdrawalDelete) {
    return new Observable(observer => {
      const docRef = this.firebaseService.firestore.doc(`${IDocumentDeleteCollections.Withdrawals}/${withdrawalDelete.withdrawalId}`);
      docRef.get().then((docSnapshot) => {
        this.handleDeletedDocumentState(docSnapshot.data(), observer);
      }).catch(_ => {
        this.firebaseService.addDocumentWithID(
          withdrawalDelete, this.firebaseService.getCollection(IDocumentDeleteCollections.Withdrawals), withdrawalDelete.withdrawalId,
        ).then(() => {
          const subscription = docRef.onSnapshot(doc => {
            if (!doc.exists) {
              observer.next(false);
              subscription();
              return;
            }
            const withdrawalDeleteDoc = doc.data();
            this.handleDeletedDocumentState(withdrawalDeleteDoc, observer);
          },
          () => {
            observer.next(false);
            subscription();
          });
        });
      });
    });
  }

  private handleDeletedDocumentState(docData: any, observer: any) {
    if (docData.status === IDocumentDeleteStatus.Failed) {
      observer.next(docData.errorReason);
    } else if (docData.status === IDocumentDeleteStatus.Complete) {
      observer.next(true);
    } else if (docData.cancelInProgress) {
      observer.next(IDocumentDeleteStatus.CancelInProgress);
    } else if (docData.status === IDocumentDeleteStatus.Created) {
      observer.next(IDocumentDeleteStatus.Created);
    } else {
      observer.next(false);
    }
  }

  deleteWithdrawalPromise(withdrawalId: string, userId: string): Promise<any> {
    return new Promise((resolve) => {
      const subscription = this.deleteWithdrawal({
        withdrawalId,
        userId,
        status: IDocumentDeleteStatus.Created,
      }).subscribe((response) => {
        if (response === IDocumentDeleteStatus.Created) { return; }
        resolve(response);
        subscription.unsubscribe();
      });
    });
  }

  saveRedistributeDeposit(depositRedistribution: IDepositRedistribution) {
    return new Observable(observer => {
      this.firebaseService.addDocumentWithID(
        depositRedistribution, this.firebaseService.getCollection('depositRedistributions'), depositRedistribution.id)
      .then(() => {
        const subscription = this.firebaseService.firestore.doc(`depositRedistributions/${depositRedistribution.id}`).onSnapshot((doc) => {
          if (doc.data().status === 'complete') {
            observer.next(true);
            subscription();
          }
        },
        () => {
          observer.next(false);
          subscription();
        });
      })
      .catch(err => {
        this.firebaseService.logError(err.message);
        observer.next(false);
      });
    });
  }

  destroyMovementObservables() {
    Object.keys(this.movements).forEach(o => {
      this.movements[o] = null;
    });
  }

  getPaidKhipuTransfers(userId: string, parentType: string, id: string) {
    const observablePath = 'khipuTransfers/' + parentType + '/' + id;
    if (this.movements[observablePath]) {
      return this.movements[observablePath].asObservable();
    }
    try {
      this.movements[observablePath] = new BehaviorSubject<IDeposit[]>(null);
      const collection = this.firebaseService.getCollection('khipuTransfers');
      const query = collection.where('status', '==', 'paid').where('userId', '==', userId);
      const parseDepositsFn = (querySnapshot) => {
        const movementList = querySnapshot.docs.filter(k => !k.data().isStockTransfer).map((document) => {
          return {
            ...document.data(),
            id: document.id,
            type: 'Depósito',
            operation: 'khipuTransfer',
            typeClass: 'pending-contribution',
            status: 'created',
            amount: parentType === 'portfolios' ? undefined : document.data().amount,
          } as IDeposit;
        });
        if (parentType === 'portfolios') {
          return movementList.filter(k => k.contributionStrategy.portfolios.find(p => p.portfolioId === id && p.percentage > 0));
        }
        return movementList;
      };
      this.firebaseService.observeQuery(query, this.movements[observablePath], parseDepositsFn);
      return this.movements[observablePath].asObservable();
    } catch (err) {
      throw err;
    }
  }

  async getDwTrades(userId: string, subscription: string) {
    if (subscription !== 'pro') {
      const firstDayOfMonth = moment.tz('America/Santiago').startOf('month').
        set({hour: 0, minute: 0, second: 0, millisecond: 0}).toDate();
      const collection = this.firebaseService.getCollection('orders');
      const query = collection.where('userId', '==', userId).where('createdAt', '>=', firstDayOfMonth).where('isDWOrder', '==', true);
      const parseTrades = (querySnapshot) => querySnapshot.docs
        .filter(doc =>
          (
            doc.data().isBuy ||
            doc.data().isSell ||
            (!doc.data().isCashInBuyingPower && !doc.data().isCashOutBuyingPower)
          ) && !doc.data().proOrder && !isBrokerRejectedOrder(doc.data()),
        )
        .map(doc => doc.data());
      this.dwTradesSub = this.firebaseService.observeQuery(query, this.dwTradesSubSource, parseTrades);
    }
  }

  getPortfolioShareVariationsInProgress(portfolioId: string, userId: string) {
    try {
      const collection = this.firebaseService.getCollection('orders');
      return collection.where('portfolioId', '==', portfolioId)
      .where('status', '==', 'created').where('resource', '==', 'withdrawal').where('userId', '==', userId).get()
      .then(queryResponse => {
        const list = [];
        if (queryResponse.docs.length > 0) {
          queryResponse.forEach(query => {
            query.data().proposedShareVariations.forEach(q => {
              if (q.assetId !== 'CASH') {
                const isAssetId = list.find(a => a.assetId === q.assetId);
                if (isAssetId) {
                  isAssetId.shares += q.shareVariation;
                } else {
                  list.push({assetId: q.assetId, shares: q.shareVariation});
                }
              }
            });
          });
          return list;
        }
        return [];
      });
    } catch (err) {
      throw err;
    }
  }

  createKushkiSubscriptionIntent(intent) {
    return new Observable(observer => {
      const collection = this.firebaseService.getCollection('kushkiSubscriptionIntents');
      Promise.all([this.firebaseService.addDocument(intent, collection)])
      .then((ref) => {
        const unsubscribe = this.firebaseService.firestore.doc(`/kushkiSubscriptionIntents/${ref[0].id}`).onSnapshot(
          (doc) => {
            if (doc.data().status === 'created' && !doc.data().deviceToken) {
              observer.next({status: 'created', subscriptionId: doc.data().subscriptionId, id: ref[0].id});
            }
            if (doc.data().status === 'valid') {
              observer.next({valid: true});
              unsubscribe();
            }
            if (doc.data().status === 'invalid') {
              observer.next({valid: false});
              unsubscribe();
            }
          },
          () => {
            observer.next({valid: false});
            unsubscribe();
          },
        );
      })
      .catch(err => {
        this.firebaseService.logError(err.message);
        observer.next({valid: false});
      });
    });
  }

  async updateKushkiSubscriptionIntent(token: string, id: string, error: boolean) {
    const data = {deviceToken: token};
    if (error) {
      data['error'] = true;
    }
    return this.firebaseService.updateDocument(data, this.firebaseService.getCollection('kushkiSubscriptionIntents'), id);
  }
}
